import React, { useState } from 'react';
import { Save, X, Eye, FileText, Code } from 'lucide-react';

interface EmailTemplate {
  id: number;
  templateType: string;
  language: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  availableVariables: string[];
  isActive: boolean;
  version: string;
}

interface TemplateEditorProps {
  template: EmailTemplate;
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
}

export default function TemplateEditor({ 
  template, 
  onSave, 
  onCancel 
}: TemplateEditorProps) {
  const [editedTemplate, setEditedTemplate] = useState<EmailTemplate>(template);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');

  const availableVariables = [
    'subscriber_email',
    'settings_url',
    'unsubscribe_url',
    'verification_url',
    'risk_score',
    'articles',
    'categories',
    'date_range',
    'language',
    'timezone',
    'site_name',
    'site_url',
  ];

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      
      setEditedTemplate({
        ...editedTemplate,
        [previewMode === 'html' ? 'htmlContent' : 'textContent']: newText,
      });
      
      // Reset cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
      }, 0);
    }
  };

  const handleSave = () => {
    onSave(editedTemplate);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Edit Template: {template.templateType} ({template.language.toUpperCase()})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('editor')}
            className={`px-3 py-1 rounded-md ${
              activeTab === 'editor'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <Code size={16} className="inline mr-1" />
            Editor
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-3 py-1 rounded-md ${
              activeTab === 'preview'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <Eye size={16} className="inline mr-1" />
            Preview
          </button>
        </div>
      </div>

      {activeTab === 'editor' ? (
        <>
          {/* Template Metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Subject</label>
              <input
                type="text"
                value={editedTemplate.subject}
                onChange={(e) => setEditedTemplate({...editedTemplate, subject: e.target.value})}
                className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Version</label>
              <input
                type="text"
                value={editedTemplate.version}
                onChange={(e) => setEditedTemplate({...editedTemplate, version: e.target.value})}
                className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
          </div>

          {/* Content Type Toggle */}
          <div className="flex space-x-2">
            <button
              onClick={() => setPreviewMode('html')}
              className={`flex items-center gap-2 px-3 py-1 rounded-md ${
                previewMode === 'html'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <Eye size={16} />
              HTML
            </button>
            <button
              onClick={() => setPreviewMode('text')}
              className={`flex items-center gap-2 px-3 py-1 rounded-md ${
                previewMode === 'text'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <FileText size={16} />
              Text
            </button>
          </div>

          {/* Variables Helper */}
          <div className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
            <h4 className="text-sm font-medium mb-2">Available Variables (click to insert)</h4>
            <div className="flex flex-wrap gap-2">
              {availableVariables.map((variable) => (
                <button
                  key={variable}
                  onClick={() => insertVariable(variable)}
                  className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                >
                  {`{{${variable}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {previewMode === 'html' ? 'HTML Content' : 'Text Content'}
            </label>
            <textarea
              id="template-content"
              value={previewMode === 'html' ? editedTemplate.htmlContent : editedTemplate.textContent}
              onChange={(e) => setEditedTemplate({
                ...editedTemplate,
                [previewMode === 'html' ? 'htmlContent' : 'textContent']: e.target.value,
              })}
              className="w-full h-96 p-3 border rounded-md font-mono text-sm dark:bg-gray-700 dark:border-gray-600"
              placeholder={`Enter ${previewMode} content here...`}
            />
          </div>

          {/* Status Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={editedTemplate.isActive}
              onChange={(e) => setEditedTemplate({...editedTemplate, isActive: e.target.checked})}
              className="rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              Template is active
            </label>
          </div>
        </>
      ) : (
        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
          <p className="text-center text-gray-500 dark:text-gray-400">
            Preview functionality would show rendered template here
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          <Save size={16} />
          Save Template
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
        >
          <X size={16} />
          Cancel
        </button>
      </div>
    </div>
  );
}